import toast from 'react-hot-toast'

import { useToastedMutation } from '@fv/client-components'
import {
  shipperUri,
  showApiError,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { AccountUserMutationDto } from '@fv/models'
import type { AccountUser } from '@fv/models/admin'

import { adminFetch } from '@/utils'

import { accountQueryKeys } from '../queries'
import type { AccountUserModel } from '../types'
import { accountUsersQueryKeys } from './queries'

type UnlockArgs = {
  accountId: string
  userId: string
}
export const useUnlockUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, userId }: UnlockArgs) =>
      adminFetch(`/accounts/${accountId}/users/${userId}/unlock`, {
        method: 'PUT',
      }),
    onError: err => {
      showApiError('There was an error unlocking the user', err)
    },
    onSuccess: (resp, { accountId }) => {
      toast.success('Unlocked User')
      queryClient.invalidateQueries({
        queryKey: accountQueryKeys.id(accountId),
      })
    },
  })
}

type FreeUpArgs = {
  accountId: string
  userId: string
}
export const useFreeUpEmail = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, userId }: FreeUpArgs) =>
      adminFetch(`/accounts/${accountId}/users/${userId}/free-up-email`, {
        method: 'PUT',
      }),
    onError: err => {
      showApiError('There was an error freeing up the email address', err)
    },
    onSuccess: (resp, { accountId }) => {
      toast.success('Changed email')
      queryClient.invalidateQueries({
        queryKey: accountQueryKeys.id(accountId),
      })
    },
  })
}

type ToggleMarketingArgs = {
  userId: string
  subscribed: boolean
}
export const useToggleMarketing = (accountId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, subscribed }: ToggleMarketingArgs) =>
      adminFetch(`/accounts/${accountId}/users/${userId}/marketing`, {
        method: 'PUT',
        body: {
          subscribed,
        },
      }),
    onMutate({ userId, subscribed }) {
      queryClient.setQueryData<AccountUserModel[]>(
        accountUsersQueryKeys.account(accountId),
        prev =>
          prev.map(u => ({
            ...u,
            marketing: {
              subscribed:
                u._id === userId ? subscribed : u.marketing?.subscribed,
            },
          })),
      )
    },
    onSettled(result, error, { userId, subscribed }) {
      if (error) {
        showApiError(`Could not edit update marketing subscription`, error)
        // revert optimistic
        queryClient.setQueryData<AccountUserModel[]>(
          accountUsersQueryKeys.account(accountId),
          prev =>
            prev.map(u => ({
              ...u,
              marketing: {
                subscribed:
                  u._id === userId ? !subscribed : u.marketing?.subscribed,
              },
            })),
        )
      }
    },
  })
}

export const useSetOwner = (accountId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      adminFetch<AccountUserModel[]>(`/accounts/${accountId}/users/owner`, {
        method: 'PUT',
        body: {
          userId,
        },
      }),
    onSuccess(users) {
      queryClient.setQueryData<AccountUserModel[]>(
        accountUsersQueryKeys.account(accountId),
        users,
      )
    },
    onError(error) {
      showApiError(`Could not make user owner`, error)
    },
  })
}

type ViewAsProps = {
  userId: string
  accountId: string
  redirect?: string
}

export const useViewAsShipper = () => {
  return useMutation({
    mutationFn: ({ userId, accountId }: ViewAsProps) =>
      adminFetch<AccountUser[]>(
        `/accounts/${accountId}/users/${userId}/view-as`,
        {
          method: 'POST',
        },
      ),
    onSuccess(_response, { redirect }) {
      window.open(
        `${shipperUri}/${redirect || ''}`,
        '_blank',
        'noopener,noreferrer',
      )
    },
    onError(error) {
      showApiError(`Could not view as user`, error)
    },
  })
}

const putUser =
  (accountId: string, userId: string) => (body: AccountUserMutationDto) =>
    adminFetch(`/accounts/${accountId}/users/${userId}`, {
      method: 'PUT',
      body,
    })

export const useUpdateAccountUser = (accountId: string, userId: string) => {
  return useToastedMutation(putUser(accountId, userId), {
    toastErrorPrefix: () => 'There was an error updating the user',
    toastSuccess: () => 'Updated user',
  })
}
